type ApiError = {
  status: number
  message: string
  body?: unknown
}

// ============= Constants =============
const CLIENT_VERSION = 'web/1.0.0'
const CLIENT_SOURCE = 'web'
const CLIENT_LOCALE = 'vi_VN'
const CLIENT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Ho_Chi_Minh'

// ============= UUID v7 Generator =============
function generateUUIDv7(): string {
  const timestamp = Date.now()
  const timestampHex = timestamp.toString(16).padStart(12, '0')

  // Random bytes for the rest
  const randomBytes = new Uint8Array(10)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(randomBytes)
  } else {
    for (let i = 0; i < 10; i++) {
      randomBytes[i] = Math.floor(Math.random() * 256)
    }
  }

  // Format: tttttttt-tttt-7xxx-yxxx-xxxxxxxxxxxx
  // t = timestamp, 7 = version, y = variant (8,9,a,b)
  const hex = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('')

  return `${timestampHex.slice(0, 8)}-${timestampHex.slice(8, 12)}-7${hex.slice(0, 3)}-${(8 + (parseInt(hex.slice(3, 4), 16) % 4)).toString(16)}${hex.slice(4, 7)}-${hex.slice(7, 19)}`
}

// ============= Helper Functions =============
function normalizeBaseUrl(baseUrl: string) {
  return baseUrl.replace(/\/+$/, '')
}

function isLocalHostname(hostname: string) {
  const normalized = String(hostname || '').trim().toLowerCase()
  return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1'
}

function resolveDevUrlForCurrentHost(devUrl: string) {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return normalizeBaseUrl(devUrl)
  }

  const currentHost = window.location.hostname
  if (!currentHost || isLocalHostname(currentHost)) {
    return normalizeBaseUrl(devUrl)
  }

  try {
    const parsed = new URL(devUrl)
    if (!isLocalHostname(parsed.hostname)) {
      return normalizeBaseUrl(devUrl)
    }

    parsed.hostname = currentHost
    return normalizeBaseUrl(parsed.toString())
  } catch {
    return normalizeBaseUrl(devUrl)
  }
}

function isCrossOriginUrl(url: string) {
  if (typeof window === 'undefined' || !window?.location?.href) return false
  try {
    return new URL(url, window.location.href).origin !== window.location.origin
  } catch {
    return false
  }
}

function readCookie(name: string) {
  if (typeof document === 'undefined') return ''
  const parts = document.cookie.split(';')
  for (const part of parts) {
    const idx = part.indexOf('=')
    if (idx === -1) continue
    const k = part.slice(0, idx).trim()
    if (k !== name) continue
    return decodeURIComponent(part.slice(idx + 1).trim())
  }
  return ''
}

function extractCsrfFromBody(body: any): string {
  if (!body || typeof body !== 'object') return ''

  const direct = typeof body?.csrf_token === 'string' ? body.csrf_token.trim() : ''
  if (direct) return direct

  const list = Array.isArray(body?.data) ? body.data : []
  const first = list[0]
  const nested = typeof first?.csrf_token === 'string' ? first.csrf_token.trim() : ''
  return nested
}

function syncCsrfFromResponse(resp: Response, body?: any) {
  const tokenFromHeader = String(resp.headers.get('X-CSRF-Token') || resp.headers.get('x-csrf-token') || '').trim()
  if (tokenFromHeader) {
    globalCsrfToken = tokenFromHeader
    return
  }

  const tokenFromBody = extractCsrfFromBody(body)
  if (tokenFromBody) {
    globalCsrfToken = tokenFromBody
  }
}

async function ensureCsrfCookie(baseUrl: string) {
  if (typeof document === 'undefined') return
  const existing = readCookie('csrf_token')
  if (existing || globalCsrfToken) return

  const url = baseUrl ? `${baseUrl}/api/auth/csrf` : '/api/auth/csrf'
  try {
    const resp = await fetch(url, { method: 'GET', credentials: 'include' })
    let body: any = null
    try {
      body = await resp.clone().json()
    } catch {
      body = null
    }
    syncCsrfFromResponse(resp, body)
  } catch {
    return
  }
}

// Determine API base URL based on environment
function getApiBaseUrl(): string {
  const isDev = import.meta.env.DEV

  const apiUrl = import.meta.env.VITE_API_URL
  if (apiUrl && String(apiUrl).trim()) {
    return normalizeBaseUrl(String(apiUrl).trim())
  }

  if (isDev) {
    // Development mode - use local backend
    const devUrl = import.meta.env.VITE_API_DEV_URL
    if (!devUrl || !String(devUrl).trim()) return 'http://localhost:3000'
    return resolveDevUrlForCurrentHost(String(devUrl).trim())
  } else {
    // Production mode - use production backend
    const prodUrl = import.meta.env.VITE_API_BASE_URL
    return prodUrl ? normalizeBaseUrl(prodUrl.trim()) : ''
  }
}

const API_BASE_URL = getApiBaseUrl()

// Log which backend is being used
console.log(`[API] Using backend: ${API_BASE_URL} (${import.meta.env.DEV ? 'development' : 'production'} mode)`)

// Auth is handled entirely via HttpOnly cookies (digigo_access, digigo_refresh).
// No access token is stored in JS — cookies are sent automatically via credentials: 'include'.
let globalCsrfToken = ''
let unauthorizedNotified = false

/**
 * @deprecated Access token is now handled via HttpOnly cookies.
 * This function is kept for backwards compatibility but does nothing.
 */
export function setGlobalAccessToken(_token: string) {
  // no-op: access token is managed via cookies
  if (_token) {
    unauthorizedNotified = false
  }
}

export function resolveBaseUrl(): string {
  return API_BASE_URL
}

interface RequestMeta {
  timestamp: string
  request_id: string
  idempotency_key?: string
  source: string
  version: string
  locale: string
  timezone: string
}

function createRequestMeta(method: string): RequestMeta {
  const needsIdempotency = ['POST', 'PUT', 'PATCH'].includes(method)
  return {
    timestamp: new Date().toISOString(),
    request_id: generateUUIDv7(),
    idempotency_key: needsIdempotency ? generateUUIDv7() : undefined,
    source: CLIENT_SOURCE,
    version: CLIENT_VERSION,
    locale: CLIENT_LOCALE,
    timezone: CLIENT_TIMEZONE
  }
}

export async function apiFetch(path: string, init?: RequestInit) {
  const base = API_BASE_URL
  let url = path.startsWith('http')
    ? path
    : `${base}${path.startsWith('/') ? path : `/${path}`}`

  const method = String(init?.method || 'GET').toUpperCase()
  if (method !== 'GET' && method !== 'HEAD') {
    await ensureCsrfCookie(base)
  }

  const csrf = method === 'GET' || method === 'HEAD' ? '' : (readCookie('csrf_token') || globalCsrfToken)
  const initHeaders = (init?.headers || {}) as any
  const hasCsrfHeader =
    typeof initHeaders?.['X-CSRF-Token'] === 'string' ||
    typeof initHeaders?.['x-csrf-token'] === 'string'

  // Metadata handling
  // If headers already contain Request-ID (from apiPostJson wrapping), reuse it.
  // Otherwise generate new meta.
  let requestId = initHeaders['X-Request-ID']
  let idempotencyKey = initHeaders['X-Idempotency-Key']
  let timestamp = initHeaders['X-Request-Timestamp']

  // If not provided in init, generate fresh
  if (!requestId) {
    const meta = createRequestMeta(method)
    requestId = meta.request_id
    idempotencyKey = meta.idempotency_key // might be undefined for GET
    timestamp = meta.timestamp // ISO string
  } else {
    // If provided, we assume timestamp is also provided or we gen it?
    // Usually apiPostJson will pass all standard headers if it generated meta.
    // Ensure timestamp if missing
    if (!timestamp) timestamp = new Date().toISOString()
  }

  // Handle Query Params for GET/DELETE (append metadata)
  if (method === 'GET' || method === 'DELETE') {
    const urlObj = new URL(url)
    urlObj.searchParams.append('meta_request_id', requestId)
    urlObj.searchParams.append('meta_timestamp', timestamp)
    urlObj.searchParams.append('meta_source', CLIENT_SOURCE)
    urlObj.searchParams.append('meta_version', CLIENT_VERSION)
    urlObj.searchParams.append('meta_locale', CLIENT_LOCALE)
    urlObj.searchParams.append('meta_timezone', CLIENT_TIMEZONE)
    url = urlObj.toString()
  }

  const headers: any = {
    ...initHeaders,
    ...(csrf && !hasCsrfHeader ? { 'X-CSRF-Token': csrf } : {}),
    // === Metadata Headers ===
    'X-Request-ID': requestId,
    'X-Request-Timestamp': timestamp,
    'X-Client-Version': CLIENT_VERSION,
    'X-Client-Timezone': CLIENT_TIMEZONE,
    ...(idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {}),
  }

  // No Bearer header — auth is via HttpOnly cookie digigo_access

  const isCross = isCrossOriginUrl(url)
  if (isCross && (method === 'GET' || method === 'HEAD' || method === 'DELETE')) {
    delete headers['X-Request-ID']
    delete headers['X-Request-Timestamp']
    delete headers['X-Client-Version']
    delete headers['X-Client-Timezone']
    delete headers['X-Idempotency-Key']
    delete headers['X-CSRF-Token']
  }

  return fetch(url, {
    ...init,
    credentials: 'include',
    headers,
  })
}

// Callback for handling unauthorized (401) errors
let unauthorizedCallback: (() => void) | null = null

export function setUnauthorizedCallback(cb: () => void) {
  unauthorizedCallback = cb
  unauthorizedNotified = false
}

type RequestInitWithAuthRetry = RequestInit & { __authRetry?: boolean }
let refreshInFlight: Promise<string | null> | null = null

function notifyUnauthorizedOnce() {
  if (!unauthorizedCallback || unauthorizedNotified) return
  unauthorizedNotified = true
  unauthorizedCallback()
}

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      console.log('[API] 401 detected, attempting to refresh token...')

      const refreshResp = await apiFetch('/api/auth/refresh', { method: 'POST' })
      if (!refreshResp.ok) {
        return null
      }

      const refreshData = await refreshResp.json()
      if (!refreshData?.success) {
        return null
      }

      // Cookies are set by the server response (digigo_access, digigo_refresh)
      // No need to extract or store tokens in JS
      console.log('[API] Token refresh successful (cookies updated by server).')
      return 'ok'
    })()
      .catch((e) => {
        console.error('[API] Refresh token failed:', e)
        return null
      })
      .finally(() => {
        refreshInFlight = null
      })
  }

  const result = await refreshInFlight
  return result !== null
}

export async function apiRequestJson<T>(path: string, init: RequestInit): Promise<T> {
  const resp = await apiFetch(path, init)
  const text = await resp.text()
  let body: any = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = null
  }
  syncCsrfFromResponse(resp, body)

  // Handle 401 with auto-refresh logic
  if (resp.status === 401) {
    const initWithRetry = init as RequestInitWithAuthRetry
    const alreadyRetried = Boolean(initWithRetry.__authRetry)

    // Auth endpoints should not trigger refresh or unauthorized notification
    const isAuthEndpoint = path.includes('/api/auth/refresh') || path.includes('/api/auth/logout') || path.includes('/api/auth/login') || path.includes('/api/auth/me')
    if (isAuthEndpoint) {
      // Only trigger unauthorized notification for refresh/logout failure;
      // login and /me should silently fail (login shows its own error, /me is expected to 401 when not logged in)
      if (!path.includes('/api/auth/login') && !path.includes('/api/auth/me')) {
        notifyUnauthorizedOnce()
      }
      const err: ApiError = {
        status: 401,
        message: path.includes('/api/auth/login') ? 'Thông tin đăng nhập không chính xác' : 'Phiên đăng nhập hết hạn',
        body: body
      }
      throw err
    }

    // If we already retried, give up
    if (alreadyRetried) {
      notifyUnauthorizedOnce()
      const err: ApiError = {
        status: 401,
        message: 'Phiên đăng nhập hết hạn',
        body: body
      }
      throw err
    }

    // Try to refresh (HttpOnly cookie digigo_refresh is sent automatically)
    const refreshOk = await refreshAccessToken()
    if (refreshOk) {
      console.log('[API] Retrying original request after token refresh...')
      const retryInit: RequestInitWithAuthRetry = { ...init, __authRetry: true }
      return apiRequestJson<T>(path, retryInit)
    }

    notifyUnauthorizedOnce()
    const err: ApiError = {
      status: 401,
      message: 'Phiên đăng nhập hết hạn',
      body: body
    }
    throw err
  }

  if (!resp.ok) {
    // Extract error message: prefer body.message (API standard), fallback to body.error, then HTTP status
    const errorMessage =
      (typeof (body as any)?.message === 'string' && (body as any).message) ||
      (typeof (body as any)?.error === 'string' && (body as any).error) ||
      `HTTP ${resp.status}`
    const err: ApiError = {
      status: resp.status,
      message: errorMessage,
      body,
    }
    throw err
  }

  return body as T
}

export async function apiGetJson<T>(path: string): Promise<T> {
  return apiRequestJson<T>(path, { method: 'GET' })
}

export async function apiPostJsonRaw<T>(path: string, payload: unknown): Promise<T> {
  const meta = createRequestMeta('POST')

  const headers: any = {
    'Content-Type': 'application/json',
    'X-Request-ID': meta.request_id,
    'X-Request-Timestamp': meta.timestamp,
    'X-Idempotency-Key': meta.idempotency_key,
    'X-Client-Version': meta.version,
    'X-Client-Timezone': meta.timezone
  }

  return apiRequestJson<T>(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload ?? {}),
  })
}

export async function apiPostJson<T>(path: string, payload: unknown): Promise<T> {
  // Generate metadata first
  const meta = createRequestMeta('POST')

  // Wrap payload
  const wrappedBody = {
    meta: {
      timestamp: meta.timestamp,
      request_id: meta.request_id,
      idempotency_key: meta.idempotency_key,
      source: meta.source,
      version: meta.version,
      locale: meta.locale,
      timezone: meta.timezone
    },
    data: payload ?? {}
  }

  // Pass metadata headers explicitly to apiFetch so they match the body
  const headers: any = {
    'Content-Type': 'application/json',
    'X-Request-ID': meta.request_id,
    'X-Request-Timestamp': meta.timestamp,
    'X-Idempotency-Key': meta.idempotency_key,
    'X-Client-Version': meta.version,
    'X-Client-Timezone': meta.timezone
  }

  return apiRequestJson<T>(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(wrappedBody),
  })
}

export async function apiPutJson<T>(path: string, payload: unknown): Promise<T> {
  const meta = createRequestMeta('PUT')
  const wrappedBody = {
    meta: {
      timestamp: meta.timestamp,
      request_id: meta.request_id,
      idempotency_key: meta.idempotency_key,
      source: meta.source,
      version: meta.version,
      locale: meta.locale,
      timezone: meta.timezone
    },
    data: payload ?? {}
  }

  const headers: any = {
    'Content-Type': 'application/json',
    'X-Request-ID': meta.request_id,
    'X-Request-Timestamp': meta.timestamp,
    'X-Idempotency-Key': meta.idempotency_key,
    'X-Client-Version': meta.version,
    'X-Client-Timezone': meta.timezone
  }

  return apiRequestJson<T>(path, {
    method: 'PUT',
    headers,
    body: JSON.stringify(wrappedBody),
  })
}

export async function apiPatchJson<T>(path: string, payload: unknown): Promise<T> {
  const meta = createRequestMeta('PATCH')
  const wrappedBody = {
    meta: {
      timestamp: meta.timestamp,
      request_id: meta.request_id,
      idempotency_key: meta.idempotency_key,
      source: meta.source,
      version: meta.version,
      locale: meta.locale,
      timezone: meta.timezone
    },
    data: payload ?? {}
  }

  const headers: any = {
    'Content-Type': 'application/json',
    'X-Request-ID': meta.request_id,
    'X-Request-Timestamp': meta.timestamp,
    'X-Idempotency-Key': meta.idempotency_key,
    'X-Client-Version': meta.version,
    'X-Client-Timezone': meta.timezone
  }

  return apiRequestJson<T>(path, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(wrappedBody),
  })
}
